import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE, IS_NATIVE_APP } from '../config/api';
import {
  emitAthenaNotificationAction,
  observeWebAthenaNotifications,
  unregisterNativeAthenaNotifications,
} from '../notifications/AthenaNotifications';
import { markNotifClicked } from '../utils/athenaNotifications';
import { clearAthenaQuickCaptureDrafts, clearNativeDeviceContent } from '../capabilities/AthenaCapabilities';
import { clearAthenaDeviceContextRuntime } from './AthenaDeviceContextProvider';
import { clearAthenaIntentRuntime } from '../platform/AthenaIntentRouter';
import { clearAthenaFloatingOverlayRuntime } from '../platform/AthenaFloatingOverlay';

const AuthContext = createContext(null);
const API = API_BASE;
export const ATHENA_AUTH_EXPIRED_EVENT = 'athena-auth-expired';

/**
 * A request made by Home, Workspace or a native entry point can discover an
 * expired access token after the app has already booted.  Keeping this signal
 * tiny avoids every surface inventing a different partial logout flow.
 */
export function signalAthenaAuthExpired(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ATHENA_AUTH_EXPIRED_EVENT, { detail }));
}

function cachedOwnerId() {
  try { return JSON.parse(localStorage.getItem('athena_user') || 'null')?.id || ''; } catch { return ''; }
}

// A sign-out and the next sign-in can be triggered close together on a mobile
// WebView. Serialising this boundary prevents an old account cleanup from
// erasing a freshly-bound account's private native state.
let athenaRuntimeCleanupTail = Promise.resolve();

/** Clears every Phase 5 local/native association for one signed-out account. */
async function clearAthenaUserScopedRuntime(ownerId) {
  const safeOwnerId = typeof ownerId === 'string' ? ownerId.trim() : String(ownerId || '').trim();
  // Without a known account, an automatic logout must not invoke a legacy
  // global native clear. The next bindOwner is the safe isolation boundary.
  if (!/^[A-Za-z0-9._:-]{1,180}$/.test(safeOwnerId)) return;
  clearAthenaQuickCaptureDrafts(safeOwnerId);
  await Promise.allSettled([
    clearNativeDeviceContent(safeOwnerId),
    clearAthenaDeviceContextRuntime(safeOwnerId),
    clearAthenaIntentRuntime(safeOwnerId),
    // The floating surface holds no account/chat content, but the explicit
    // opt-in must not survive a logout and appear for a subsequent account.
    clearAthenaFloatingOverlayRuntime(),
  ]);
}

function queueAthenaUserScopedCleanup(ownerId, beforeCleanup = null, afterCleanup = null) {
  const run = athenaRuntimeCleanupTail
    .catch(() => {})
    .then(async () => {
      if (typeof beforeCleanup === 'function') {
        try { await beforeCleanup(); } catch { /* local cleanup must still run */ }
      }
      await clearAthenaUserScopedRuntime(ownerId);
      if (typeof afterCleanup === 'function') {
        try { await afterCleanup(); } catch { /* local cleanup already completed */ }
      }
    });
  // Keep the queue usable even if an integration-specific native call fails.
  athenaRuntimeCleanupTail = run.catch(() => {});
  return run;
}

async function waitForAthenaUserScopedCleanup() {
  await athenaRuntimeCleanupTail;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const invalidatingSessionRef = useRef(false);

  const invalidateExpiredSession = useCallback(async (expectedToken = '') => {
    const currentToken = localStorage.getItem('athena_token') || '';
    // An in-flight old request must never erase a freshly-created session.
    if (expectedToken && currentToken && expectedToken !== currentToken) return;
    if (invalidatingSessionRef.current) return;
    invalidatingSessionRef.current = true;
    try {
      const cachedUser = (() => {
        try { return JSON.parse(localStorage.getItem('athena_user') || 'null'); } catch { return null; }
      })();
      const ownerId = cachedUser?.id || cachedOwnerId();
      await queueAthenaUserScopedCleanup(ownerId, async () => {
        if (!IS_NATIVE_APP || !/^[A-Za-z0-9._:-]{1,180}$/.test(String(ownerId || '').trim())) return;
        // The expired token is useful only for safe best-effort unregistering;
        // owner-scoped cleanup still runs if the server rejects it.
        await unregisterNativeAthenaNotifications(currentToken || expectedToken, { ownerId });
      });
      localStorage.removeItem('athena_token');
      localStorage.removeItem('athena_user');
      setUser(null);
    } finally {
      invalidatingSessionRef.current = false;
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const cachedUser = (() => {
      try { return JSON.parse(localStorage.getItem('athena_user') || 'null'); } catch { return null; }
    })();
    const token = localStorage.getItem('athena_token');
    try {
      if (!token) return;
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 15000,
      });
      setUser(data);
      localStorage.setItem('athena_user', JSON.stringify(data));
    } catch (error) {
      // Em celular a rede pode ainda não ter sido restaurada ao abrir a PWA.
      // Nunca apague a sessão persistida por falha de rede/timeout; somente uma
      // resposta explícita do servidor (token inválido ou vencido) faz logout.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        await invalidateExpiredSession(token);
      } else if (cachedUser) {
        // Permite iniciar a PWA offline e sincronizar a sessão quando a rede
        // voltar, sem pedir credenciais novamente.
        setUser(cachedUser);
      }
    } finally {
      setLoading(false);
    }
  }, [invalidateExpiredSession]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onExpired = (event) => {
      void invalidateExpiredSession(event?.detail?.token || '');
    };
    window.addEventListener(ATHENA_AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(ATHENA_AUTH_EXPIRED_EVENT, onExpired);
  }, [invalidateExpiredSession]);

  useEffect(() => {
    const revalidateOnResume = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') void checkAuth();
    };
    window.addEventListener('focus', revalidateOnResume);
    document.addEventListener('visibilitychange', revalidateOnResume);
    return () => {
      window.removeEventListener('focus', revalidateOnResume);
      document.removeEventListener('visibilitychange', revalidateOnResume);
    };
  }, [checkAuth]);

  useEffect(() => {
    if (!user || IS_NATIVE_APP || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // This path is PWA-only. The Android shell attaches its own native plugin
    // listeners in NativeAppShell and never starts Firebase Web Messaging.
    observeWebAthenaNotifications().catch(() => {});
    const onWorkerMessage = (event) => {
      if (event.data?.type === 'athena-notification-click') {
        const notification = emitAthenaNotificationAction(event.data);
        if (notification?.id) markNotifClicked(notification.id);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onWorkerMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onWorkerMessage);
  }, [user]);

  const login = async (email, password) => {
    await waitForAthenaUserScopedCleanup();
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem('athena_token', data.token);
    localStorage.setItem('athena_user', JSON.stringify({ id: data.id, email: data.email, name: data.name, role: data.role, avatar: data.avatar }));
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    await waitForAthenaUserScopedCleanup();
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    localStorage.setItem('athena_token', data.token);
    localStorage.setItem('athena_user', JSON.stringify({ id: data.id, email: data.email, name: data.name, role: data.role, avatar: data.avatar }));
    setUser(data);
    return data;
  };

  const logout = async () => {
    const ownerId = user?.id || cachedOwnerId();
    const token = localStorage.getItem('athena_token');
    // Queue immediately so a rapid subsequent login/register cannot bind a
    // new owner before native notification unregister and all owner-scoped
    // cleanup have completed.
    let nativeRegistration = null;
    const cleanup = queueAthenaUserScopedCleanup(ownerId, async () => {
      if (IS_NATIVE_APP && /^[A-Za-z0-9._:-]{1,180}$/.test(String(ownerId || '').trim())) {
        try {
          // The native provider unregisters while this owner is still bound,
          // then clears only that owner's local notification identity.
          const nativeResult = await unregisterNativeAthenaNotifications(token, { ownerId });
          nativeRegistration = nativeResult?.registration || null;
        } catch {
          // Logout still continues if an older/damaged APK lacks this surface.
        }
      }
    }, async () => {
      try {
        await axios.post(`${API}/auth/logout`, nativeRegistration?.deviceId && nativeRegistration?.token ? {
          device_id: nativeRegistration.deviceId,
          push_token: nativeRegistration.token,
        } : {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } catch { /* Server logout is best effort; local isolation is mandatory. */ }
    });
    await cleanup;
    localStorage.removeItem('athena_token');
    localStorage.removeItem('athena_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      // Public only for authenticated surfaces that receive an explicit 401/403
      // after boot. Network failures must keep the offline session intact.
      invalidateSession: invalidateExpiredSession,
      revalidateSession: checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be inside AuthProvider');
  return context;
}
